/**
 * Super-admin view of incomplete SAC profiles, grouped by hostel.
 * Each row shows what's missing + a "Reassign to hostel" button which marks
 * the row reassigned=true so it appears on the hostel-admin's "Fix and
 * resubmit" panel.
 */
import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowsLeftRight, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SacIncomplete() {
  const [rows, setRows] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [schools, setSchools] = useState([]);

  const refresh = () => Promise.all([
    api.get("/sac/incomplete").then((r) => setRows(r.data || [])),
    api.get("/admin/hostels").then((r) => setHostels(r.data || [])).catch(() => {}),
    api.get("/admin/schools").then((r) => setSchools(r.data || [])).catch(() => {}),
  ]).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const grouped = useMemo(() => {
    const map = {};
    for (const r of rows) {
      const key = r.hostel_id || r.school_id || "unassigned";
      if (!map[key]) map[key] = [];
      map[key].push(r);
    }
    return map;
  }, [rows]);

  const orgName = (key) => {
    const h = hostels.find((x) => x.id === key); if (h) return `${h.name} (hostel)`;
    const s = schools.find((x) => x.id === key); if (s) return `${s.name} (school)`;
    return "Unassigned";
  };

  const reassign = async (row) => {
    try {
      await api.post(`/sac/incomplete/${row.id}/reassign`);
      toast.success("Sent back to hostel/school for correction");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <DashboardLayout title="Incomplete SAC profiles" subtitle="Children waiting for missing info before they enter the Support-a-Child portal">
      {rows.length === 0 ? (
        <div className="vs-card p-10 text-center text-[var(--vs-text-secondary)]" data-testid="sac-incomplete-empty">No incomplete profiles. All transfers are clean.</div>
      ) : (
        Object.entries(grouped).map(([key, list]) => (
          <div key={key} className="vs-card overflow-x-auto mb-4" data-testid={`sac-incomplete-group-${key}`}>
            <div className="px-4 py-3 bg-[var(--vs-bg-subtle)] border-b border-[var(--vs-border)] flex items-center gap-2">
              <Warning size={14} weight="duotone" className="text-[var(--vs-warning)]" />
              <span className="font-display font-semibold">{orgName(key)}</span>
              <Badge variant="outline" className="ml-2">{list.length}</Badge>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[var(--vs-bg-subtle)]">
                <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
                  <th className="px-4 py-3">Child</th>
                  <th className="px-4 py-3">Missing</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--vs-border)]" data-testid={`sac-incomplete-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.student_name?.trim() || r.student_id?.slice(0, 8)}</div>
                      <div className="text-xs font-mono text-[var(--vs-text-secondary)]">{r.student_id?.slice(0, 8)}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{r.missing_fields?.slice(0, 4).join(", ")}{(r.missing_fields?.length || 0) > 4 ? `, +${r.missing_fields.length - 4} more` : ""}</td>
                    <td className="px-4 py-3 text-xs font-mono">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      {r.reassigned ? <Badge variant="outline" className="border-[var(--vs-secondary)]/30 text-[var(--vs-secondary)]">Reassigned</Badge> : <Badge variant="outline">Pending</Badge>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!r.reassigned && (
                        <Button size="sm" variant="outline" onClick={() => reassign(r)} data-testid={`sac-reassign-${r.id}`}>
                          <ArrowsLeftRight size={12} className="mr-1.5" /> Reassign
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </DashboardLayout>
  );
}
